import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import EditableInnerBox from '@repo/ui/editable-box'
import Typography, { TypographyProps } from '@repo/ui/typography'

import { getEnrollment } from '../../../../api/enrollments/get-enrollment'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import {
  MedicalConditionEnum,
  medicalConditionIndicatorEnumMapping
} from '../../../../lib/types/enums/enrollment/medical-condition-enum'
import {
  MedicalStatusEnum,
  medicalStatusEnumMapping
} from '../../../../lib/types/enums/enrollment/medical-status.enum'
import { MedicalAttestationTypeEnumMapping } from '../../../../lib/types/enums/medical-attestation-sending/medical-attestation-sending.enum'
import {
  enrollmentExtendedHealthFields,
  EnrollmentExtendedHealthType
} from '../../../../lib/types/fields/enrollments/enrollment'
import { DataProps } from '../../../../lib/types/utils'
import MedicalStatementActions from '../../enrollments/medical-statement-actions'

type MedicalInformationProps = {
  title: string
  information: string
  translation?: string
  textVariant?: TypographyProps['variant']
}

const MedicalInformation: React.FC<MedicalInformationProps> = ({
  title,
  information,
  translation,
  textVariant = 'tag'
}) => {
  return (
    <div className="flex flex-col gap-2 items-start">
      <Typography variant="label-medium" className="text-grey-lighter">
        {title}
      </Typography>
      <Typography variant={textVariant}>{information}</Typography>
      {translation && (
        <div className="border border-grey-light rounded p-2">
          <Typography variant="small-medium" className="text-grey-light-active">
            {translation}
          </Typography>
        </div>
      )}
    </div>
  )
}

type EnrollmentMedicalInformationProps = {
  mode: 'parent' | 'admin' | 'client'
  showEmptyInformation?: boolean
  onEdit?: () => void
  /** Called after a medical attestation action succeeded, so the host page can refresh its data. */
  onMedicalStatementChange?: () => void
}

export function EnrollmentMedicalInformation(
  props: DataProps<EnrollmentExtendedHealthType, EnrollmentMedicalInformationProps>
) {
  const {
    data: enrollment,
    mode,
    showEmptyInformation = false,
    onEdit,
    onMedicalStatementChange
  } = useDataProps(props, (id) => getEnrollment(id, enrollmentExtendedHealthFields))

  if (!enrollment) return null

  const hasPrimaryMedicalInformation = Boolean(
    enrollment.medicalInformations?.trim() || enrollment.medicalInformationsEn?.trim()
  )
  const hasAdditionalMedicalInformation = Boolean(
    enrollment.additionalMedicalInformations?.trim() ||
    enrollment.additionalMedicalInformationsEn?.trim()
  )
  const showPrimaryMedicalInformation =
    hasPrimaryMedicalInformation && enrollment.medicalLevel !== MedicalConditionEnum.RAS
  const hasMedicalAttestation = Boolean(enrollment.medicalAttestationSending)

  if (!enrollment.medicalStatus && !showEmptyInformation) return null
  if (
    !showEmptyInformation &&
    !showPrimaryMedicalInformation &&
    !hasAdditionalMedicalInformation &&
    !hasMedicalAttestation
  )
    return null

  const variant = enrollment.medicalStatus
    ? medicalStatusEnumMapping[enrollment.medicalStatus].variant
    : 'grey'

  const getPreferredInformation = (translated?: string, original?: string) => {
    if (mode !== 'admin') return original
    return original?.trim() || translated
  }

  const preferredMedicalInformation = getPreferredInformation(
    enrollment.medicalInformationsEn,
    enrollment.medicalInformations
  )
  const primaryMedicalInformation = preferredMedicalInformation?.trim()
    ? preferredMedicalInformation
    : '-'

  const secondaryMedicalInformation =
    mode === 'admin' && enrollment.medicalInformations?.trim()
      ? enrollment.medicalInformationsEn
      : undefined

  const preferredAdditionalMedicalInformation = getPreferredInformation(
    enrollment.additionalMedicalInformationsEn,
    enrollment.additionalMedicalInformations
  )
  const primaryAdditionalMedicalInformation = preferredAdditionalMedicalInformation?.trim()
    ? preferredAdditionalMedicalInformation
    : '-'

  const secondaryAdditionalMedicalInformation =
    mode === 'admin' && enrollment.additionalMedicalInformations?.trim()
      ? enrollment.additionalMedicalInformationsEn
      : undefined
  const signedMedicalAttestation = enrollment.medicalAttestationSending?.signed
    ? enrollment.medicalAttestationSending
    : undefined
  const sentMedicalAttestation =
    enrollment.medicalAttestationSending && !enrollment.medicalAttestationSending.signed
      ? enrollment.medicalAttestationSending
      : undefined

  return (
    <EditableInnerBox
      containerMode={mode === 'client' ? 'parent' : mode}
      title={'Informations sur la santé'}
      mode={'none'}
      onEdit={onEdit}
      subtitle={
        mode === 'admin' && (
          <div className={'flex items-center gap-3'}>
            <Badge
              size={'small'}
              variant={medicalConditionIndicatorEnumMapping[enrollment.medicalLevel].variant}
              text={medicalConditionIndicatorEnumMapping[enrollment.medicalLevel].label}
            />

            {(hasPrimaryMedicalInformation || hasAdditionalMedicalInformation) && (
              <MedicalStatementActions
                enrollmentId={enrollment.id}
                medicalStatus={enrollment.medicalStatus}
                additionalMedicalInformations={enrollment.additionalMedicalInformations}
                additionalMedicalInformationsEn={enrollment.additionalMedicalInformationsEn}
                onSuccess={onMedicalStatementChange}
              />
            )}
          </div>
        )
      }
    >
      <div className={'flex gap-2 mt-2'}>
        {enrollment.medicalStatus !== MedicalStatusEnum.ATTESTATION_SIGNED &&
          (enrollment.medicalStatus === MedicalStatusEnum.DECLARATION_SIGNED ||
            mode !== 'admin') &&
          enrollment.declarationSignedAt && (
            <Badge
              size={'small'}
              variant={mode !== 'admin' ? 'success' : variant}
              text={`Déclaration signée le ${DateFormatter.DDMMYYYY(enrollment.declarationSignedAt)}`}
            />
          )}

        {signedMedicalAttestation?.acceptedAt && (
          <Badge
            size={'small'}
            variant={mode !== 'admin' ? 'success' : variant}
            text={`Attestation ${MedicalAttestationTypeEnumMapping[signedMedicalAttestation.type].toLowerCase()} signée le ${DateFormatter.DDMMYYYY(signedMedicalAttestation.acceptedAt)}`}
          />
        )}

        {sentMedicalAttestation &&
          (enrollment.medicalStatus === MedicalStatusEnum.ATTESTATION_PENDING ||
            enrollment.medicalStatus === MedicalStatusEnum.PENDING_LEC_MASTER_VALIDATION) && (
            <Badge
              size={'small'}
              variant={mode !== 'admin' ? 'warning' : variant}
              text={`Attestation ${MedicalAttestationTypeEnumMapping[sentMedicalAttestation.type].toLowerCase()} envoyée`}
            />
          )}
      </div>

      {(showEmptyInformation || showPrimaryMedicalInformation) && (
        <>
          <div className={'h-3'} />
          <MedicalInformation
            textVariant={mode !== 'admin' ? 'label' : 'tag'}
            title={"Information saisie lors de la demande d'inscription"}
            translation={secondaryMedicalInformation}
            information={primaryMedicalInformation}
          />
          <div className={'h-3'} />
        </>
      )}

      {(showEmptyInformation || hasAdditionalMedicalInformation) && (
        <MedicalInformation
          textVariant={mode !== 'admin' ? 'label' : 'tag'}
          title={'Information complémentaire'}
          information={primaryAdditionalMedicalInformation}
          translation={secondaryAdditionalMedicalInformation}
        />
      )}
    </EditableInnerBox>
  )
}
